import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, User } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";

type Mode = "login" | "signup";

export default function LoginPage() {
  const {
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
  } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await signInWithEmail(email, password);
      } else {
        await signUpWithEmail(email, password, firstName, lastName);
      }
      navigate("/");
    } catch (err: any) {
      setError(err.message ?? "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    try {
      await signInWithGoogle();
      navigate("/");
    } catch (err: any) {
      setError(err.message ?? "Google sign-in failed");
    }
  };

  const handleApple = async () => {
    setError("");
    try {
      await signInWithApple();
      navigate("/");
    } catch (err: any) {
      setError(err.message ?? "Apple sign-in failed");
    }
  };

  return (
    <div className='min-h-screen bg-primary-800 flex flex-col items-center justify-center px-6 py-12'>
      {/* Logo / Brand */}
      <div className='text-center mb-10'>
        <img
          src='/logo.png'
          alt='Golf Kraken'
          className='w-24 h-24 rounded-3xl object-contain mx-auto mb-4 shadow-lg'
        />
        <h1 className='text-3xl font-bold text-white'>Golf Kraken</h1>
        <p className='text-white/60 mt-1 text-sm'>
          Games, Friends, and AI Insights
        </p>
      </div>

      {/* Card */}
      <div className='w-full max-w-sm bg-white rounded-3xl shadow-xl p-6'>
        {/* Social Sign-in */}
        <div className='flex flex-col gap-3 mb-6'>
          <Button
            variant='outline'
            fullWidth
            onClick={handleGoogle}
            leftIcon={
              <svg viewBox='0 0 24 24' width='18' height='18'>
                <path
                  fill='#4285F4'
                  d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                />
                <path
                  fill='#34A853'
                  d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                />
                <path
                  fill='#FBBC05'
                  d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                />
                <path
                  fill='#EA4335'
                  d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                />
              </svg>
            }
          >
            Continue with Google
          </Button>

          <Button
            variant='outline'
            fullWidth
            onClick={handleApple}
            leftIcon={
              <svg
                viewBox='0 0 24 24'
                width='18'
                height='18'
                fill='currentColor'
              >
                <path d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' />
              </svg>
            }
          >
            Continue with Apple
          </Button>
        </div>

        {/* Divider */}
        <div className='flex items-center gap-3 mb-5'>
          <div className='flex-1 border-t border-gray-200' />
          <span className='text-xs text-gray-400 uppercase tracking-wide'>
            or
          </span>
          <div className='flex-1 border-t border-gray-200' />
        </div>

        {/* Email form */}
        <form onSubmit={handleEmailSubmit} className='flex flex-col gap-3'>
          {mode === "signup" && (
            <div className='flex gap-2'>
              <Input
                label='First Name'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete='given-name'
                leftIcon={<User size={16} />}
              />
              <Input
                label='Last Name'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete='family-name'
              />
            </div>
          )}

          <Input
            label='Email'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete='email'
            leftIcon={<Mail size={16} />}
          />

          <div className='relative'>
            <Input
              label='Password'
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={
                mode === "signup" ? "new-password" : "current-password"
              }
              leftIcon={<Lock size={16} />}
            />
            <button
              type='button'
              onClick={() => setShowPassword((v) => !v)}
              className='absolute right-3 bottom-2.5 text-gray-400 hover:text-gray-600'
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && <p className='text-sm text-red-500 text-center'>{error}</p>}

          <Button type='submit' fullWidth loading={loading} className='mt-1'>
            {mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </form>

        {/* Toggle mode */}
        <p className='text-center text-sm text-gray-500 mt-4'>
          {mode === "login"
            ? "Don't have an account? "
            : "Already have an account? "}
          <button
            type='button'
            onClick={() => {
              setMode(mode === "login" ? "signup" : "login");
              setError("");
            }}
            className='text-secondary-400 font-semibold hover:underline'
          >
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
